import { Injectable } from '@angular/core';

import {
   HttpInterceptor,
   HttpRequest,
   HttpHandler,
   HttpEvent,
   HttpResponse,
   HTTP_INTERCEPTORS,
} from '@angular/common/http';

import { Observable, of, Subject } from 'rxjs';
import { tap, catchError, takeUntil, finalize } from 'rxjs/operators';

import { isAssets, isFullUrl } from './httpFunctions';
import { HttpCancelService } from './http-cancel.service';

@Injectable()
export class CacheHttpInterceptorBase implements HttpInterceptor {
   constructor(
      private cancelService: HttpCancelService,
   ) {}

   inFlight = new Map<string, any>();
   svgIcons = new Map<string, any>();

   intercept(
      request: HttpRequest<any>,
      next: HttpHandler,
   ): Observable<HttpEvent<any>> {
      if ( isAssets( request.url )) {
         const svgCached = this.svgIcons.get( request.url );
         if ( svgCached ) { return of( svgCached ); }

         return next.handle( request ).pipe(
            takeUntil( this.cancelService.cancelAll ),
            tap( result => {
               if ( result instanceof HttpResponse ) {
                  this.svgIcons.set( request.url, result );
               }
            })
         );
      }

      if ( isFullUrl( request.url )) {
         return next.handle( request );
      }

      const key = request.method + request.url + ( request?.body ? JSON.stringify( request.body ) : '' );

      const cached = this.inFlight.get( key );
      if ( cached ) { return cached; }

      this.inFlight.set( key, new Subject<any>() );
      setTimeout( _ => this.inFlight.has( key ) && this.inFlight.delete( key ), 6000 );

      return next.handle( request ).pipe(
         takeUntil( this.cancelService.cancelAll ),
         tap( result => {
            if ( result instanceof HttpResponse ) {
               const c = this.inFlight.get( key );
               if ( c ) {
                  c.next( result );
               }
            }
         }),
         catchError( error => {
            throw error;
         }),
         finalize( () => {
            // Complete the Subject and remove from inFlight when request
            // completes, errors, or is cancelled via takeUntil
            const c = this.inFlight.get( key );
            if ( c ) {
               c.complete();
               this.inFlight.delete( key );
            }
         }),
      );
   }

   /**
    * Clears the in-flight request cache on logout.
    * Called by ResetService during logout to prevent stale cached requests.
    */
   reset(): void {
      // Complete all pending Subject observables to cancel duplicate request subscribers
      this.inFlight.forEach( subject => {
         if ( subject && typeof subject.complete === 'function' ) {
            subject.complete();
         }
      });
      this.inFlight.clear();
      this.svgIcons.clear();
   }
}

export let cacheHttpInterceptor = {
   provide: HTTP_INTERCEPTORS,
   useClass: CacheHttpInterceptorBase,
   multi: true
};
